import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '../../utils/api';
import { Button, Badge, Alert, SearchBar, Spinner, Modal, ScopeBadges } from './shared';
import GithubImportModal from './GithubImportModal';
import SyncPickerModal from './SyncPickerModal';

export default function AdminSkills() {
    const navigate = useNavigate();
    const [skills, setSkills] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [addMenuOpen, setAddMenuOpen] = useState(false);
    const [uploadState, setUploadState] = useState({ open: false, uploading: false, file: null, files: [], paths: [], preview: null, error: null });
    const [githubModal, setGithubModal] = useState({ open: false, mode: 'add' });
    const [hasGithubConfig, setHasGithubConfig] = useState(false);
    const [syncPickerOpen, setSyncPickerOpen] = useState(false);
    const [selectedTag, setSelectedTag] = useState('');
    const addMenuRef = useRef(null);
    const zipInputRef = useRef(null);
    const folderInputRef = useRef(null);

    useEffect(() => {
        loadSkills();
        checkGithubConfig();
    }, []);

    const checkGithubConfig = async () => {
        try {
            const result = await adminApi.getGithubConfig();
            setHasGithubConfig(!!(result.url && result.has_token));
        } catch {}
    };

    useEffect(() => {
        if (!addMenuOpen) return;
        const handler = (e) => {
            if (addMenuRef.current && !addMenuRef.current.contains(e.target)) {
                setAddMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [addMenuOpen]);

    const loadSkills = async () => {
        try {
            setLoading(true);
            const res = await adminApi.listSkills();
            setSkills(res.skills || []);
            setError(null);
        } catch (err) {
            setError(err.message || 'Failed to load skills');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (e, skillId) => {
        e.stopPropagation();
        if (!confirm(`Delete skill "${skillId}"? This cannot be undone.`)) return;
        try {
            await adminApi.deleteSkill(skillId, true);
            setSuccessMessage(`Skill "${skillId}" deleted`);
            loadSkills();
            setTimeout(() => setSuccessMessage(null), 5000);
        } catch (err) {
            setError(err.message);
        }
    };

    const handleZipSelect = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setAddMenuOpen(false);
        e.target.value = '';
        await previewUpload({ zipFile: file });
    };

    const handleFolderSelect = async (e) => {
        const fileList = Array.from(e.target.files || []);
        if (fileList.length === 0) return;
        setAddMenuOpen(false);
        e.target.value = '';
        const paths = fileList.map(f => f.webkitRelativePath || f.name);
        await previewUpload({ folderFiles: fileList, paths });
    };

    const previewUpload = async ({ zipFile = null, folderFiles = [], paths = null }) => {
        setUploadState({
            open: true, uploading: false, file: zipFile, files: folderFiles, paths,
            preview: { files: [], skillName: '', skillDescription: '', loading: true },
            error: null,
        });
        try {
            const result = await adminApi.previewUploadSkill({ zipFile, folderFiles, paths });
            if (result.validation_error) {
                setUploadState(prev => ({
                    ...prev,
                    preview: {
                        files: result.files || [],
                        skillName: result.skill_name || '',
                        skillDescription: result.description || '',
                        totalSize: result.total_size || 0,
                        exists: false,
                        existingSkill: null,
                        loading: false,
                    },
                    error: `Validation: ${result.validation_error}`,
                }));
                return;
            }
            setUploadState(prev => ({
                ...prev,
                preview: {
                    files: result.files || [],
                    skillName: result.skill_name || '',
                    skillId: result.skill_id || '',
                    skillDescription: result.description || '',
                    totalSize: result.total_size || 0,
                    exists: result.exists || false,
                    existingSkill: result.existing_skill || null,
                    loading: false,
                },
                error: null,
            }));
        } catch (err) {
            setUploadState(prev => ({
                ...prev,
                preview: { ...prev.preview, loading: false },
                error: err.message || 'Preview failed',
            }));
        }
    };

    const handleUploadConfirm = async (overwrite = false) => {
        setUploadState(prev => ({ ...prev, uploading: true, error: null }));
        try {
            if (uploadState.file) {
                await adminApi.uploadSkill({ zipFile: uploadState.file, overwrite });
            } else {
                await adminApi.uploadSkill({
                    folderFiles: uploadState.files,
                    paths: uploadState.paths,
                    overwrite,
                });
            }
            setUploadState({ open: false, uploading: false, file: null, files: [], paths: [], preview: null, error: null });
            setSuccessMessage('Skill uploaded successfully');
            loadSkills();
            setTimeout(() => setSuccessMessage(null), 5000);
        } catch (err) {
            const msg = err.message || 'Upload failed';
            setUploadState(prev => ({ ...prev, uploading: false, error: msg }));
        }
    };

    const handleGithubSuccess = (message) => {
        setSuccessMessage(message);
        loadSkills();
        checkGithubConfig();
        setTimeout(() => setSuccessMessage(null), 5000);
    };

    const allTags = Array.from(
        new Set(skills.flatMap((s) => s.tags || []))
    ).sort();

    const filteredSkills = skills.filter((s) => {
        if (selectedTag && !(s.tags || []).includes(selectedTag)) return false;
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        return s.id.toLowerCase().includes(q)
            || s.name.toLowerCase().includes(q)
            || (s.description && s.description.toLowerCase().includes(q))
            || (s.tags || []).some((t) => t.toLowerCase().includes(q));
    });

    return (
        <div>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <div>
                    <h1 className="text-lg font-semibold text-gray-900">Skills</h1>
                    <p className="text-sm text-gray-500 mt-0.5">
                        {skills.length} skill{skills.length !== 1 ? 's' : ''} — SKILL.md files that define agent capabilities
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => setSyncPickerOpen(true)}>
                        <span className="flex items-center gap-1.5">
                            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z" />
                            </svg>
                            Sync from Git
                        </span>
                    </Button>
                    {false && hasGithubConfig && (
                        <Button variant="ghost" size="sm" onClick={() => setGithubModal({ open: true, mode: 'sync' })}>
                            Legacy sync
                        </Button>
                    )}
                    <div className="relative" ref={addMenuRef}>
                        <Button size="sm" onClick={() => setAddMenuOpen(!addMenuOpen)}>
                            <span className="flex items-center gap-1">
                                + Add
                                <svg className={`w-3.5 h-3.5 transition-transform ${addMenuOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                                </svg>
                            </span>
                        </Button>
                        {addMenuOpen && (
                            <div className="absolute right-0 mt-1 w-64 bg-white rounded-lg border border-gray-200 shadow-lg z-20 py-1">
                                <button
                                    className="w-full text-left px-4 py-2.5 hover:bg-gray-50 transition-colors"
                                    onClick={() => { setAddMenuOpen(false); navigate('/admin/skills/new'); }}
                                >
                                    <div className="text-sm font-medium text-gray-900">Create manually</div>
                                    <div className="text-xs text-gray-500">Write SKILL.md content directly</div>
                                </button>
                                <button
                                    className="w-full text-left px-4 py-2.5 hover:bg-gray-50 transition-colors"
                                    onClick={() => zipInputRef.current?.click()}
                                >
                                    <div className="text-sm font-medium text-gray-900">Upload .zip</div>
                                    <div className="text-xs text-gray-500">Upload a zip file containing skill folder</div>
                                </button>
                                <button
                                    className="w-full text-left px-4 py-2.5 hover:bg-gray-50 transition-colors"
                                    onClick={() => folderInputRef.current?.click()}
                                >
                                    <div className="text-sm font-medium text-gray-900">Upload folder</div>
                                    <div className="text-xs text-gray-500">Select a skill folder from your computer</div>
                                </button>
                                <div className="border-t border-gray-100 my-1" />
                                <button
                                    className="w-full text-left px-4 py-2.5 hover:bg-gray-50 transition-colors"
                                    onClick={() => { setAddMenuOpen(false); setSyncPickerOpen(true); }}
                                >
                                    <div className="text-sm font-medium text-gray-900 flex items-center gap-1.5">
                                        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z" />
                                        </svg>
                                        Import from Sync Config
                                    </div>
                                    <div className="text-xs text-gray-500">Pick a sync config to pull skills</div>
                                </button>
                            </div>
                        )}
                        <input ref={zipInputRef} type="file" accept=".zip" className="hidden" onChange={handleZipSelect} />
                        <input ref={folderInputRef} type="file" className="hidden" webkitdirectory="" directory="" multiple onChange={handleFolderSelect} />
                    </div>
                </div>
            </div>

            {error && <Alert variant="error" onClose={() => setError(null)} className="mb-4">{error}</Alert>}
            {successMessage && <Alert variant="success" onClose={() => setSuccessMessage(null)} className="mb-4">{successMessage}</Alert>}

            <SearchBar
                value={searchInput}
                onChange={setSearchInput}
                onSearch={() => setSearchQuery(searchInput)}
                onRefresh={() => { setSearchInput(''); setSearchQuery(''); loadSkills(); }}
                placeholder="Type and press Enter or click search..."
                className="mb-3"
            />

            {allTags.length > 0 && (
                <div className="flex items-center gap-1.5 flex-wrap mb-4">
                    <span className="text-xs text-gray-500 mr-1">Quick tags:</span>
                    <button
                        type="button"
                        onClick={() => setSelectedTag('')}
                        className={`text-xs px-2 py-0.5 rounded-full border transition-colors ${
                            selectedTag === ''
                                ? 'bg-gray-900 text-white border-gray-900'
                                : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
                        }`}
                    >
                        All
                    </button>
                    {allTags.map((t) => (
                        <button
                            key={t}
                            type="button"
                            onClick={() => setSelectedTag(selectedTag === t ? '' : t)}
                            className={`text-xs px-2 py-0.5 rounded-full border transition-colors ${
                                selectedTag === t
                                    ? 'bg-blue-600 text-white border-blue-600'
                                    : 'bg-blue-50 text-blue-700 border-blue-100 hover:border-blue-300'
                            }`}
                        >
                            #{t}
                        </button>
                    ))}
                </div>
            )}

            {loading ? (
                <Spinner size="lg" text="Loading skills..." center />
            ) : filteredSkills.length === 0 ? (
                <div className="text-center py-12 text-gray-500 text-sm">No skills found</div>
            ) : (
                <div className="border border-gray-200 rounded-lg bg-white overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-gray-200 bg-gray-50/60">
                                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Skill</th>
                                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500">ID</th>
                                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500">Scope</th>
                                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500">Tags</th>
                                <th className="text-center px-3 py-2.5 text-xs font-medium text-gray-500">Resources</th>
                                <th className="text-right px-4 py-2.5 text-xs font-medium text-gray-500 w-40">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {filteredSkills.map((skill) => (
                                <tr key={skill.id} className="hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => navigate(`/admin/skills/${skill.id}`)}>
                                    <td className="px-4 py-3">
                                        <span className="font-medium text-gray-900">{skill.name}</span>
                                        {skill.description && <p className="text-xs text-gray-400 mt-0.5 truncate max-w-md">{skill.description}</p>}
                                    </td>
                                    <td className="px-3 py-3"><Badge variant="outline">{skill.id}</Badge></td>
                                    <td className="px-3 py-3">
                                        <ScopeBadges environment={skill.environment} />
                                    </td>
                                    <td className="px-3 py-3">
                                        {(skill.tags || []).length > 0 ? (
                                            <div className="flex flex-wrap gap-1">
                                                {(skill.tags || []).slice(0, 4).map((t) => (
                                                    <Badge key={t} variant="info">#{t}</Badge>
                                                ))}
                                                {skill.tags.length > 4 && <Badge variant="outline">+{skill.tags.length - 4}</Badge>}
                                            </div>
                                        ) : (
                                            <span className="text-gray-300">-</span>
                                        )}
                                    </td>
                                    <td className="px-3 py-3 text-center">
                                        {skill.resource_count > 0 ? (
                                            <Badge variant="outline">{skill.resource_count}</Badge>
                                        ) : (
                                            <span className="text-gray-300">-</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                                            <Button variant="ghost" size="xs" onClick={() => navigate(`/admin/skills/${skill.id}/edit`)}>Edit</Button>
                                            <Button variant="ghost" size="xs" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={(e) => handleDelete(e, skill.id)}>Delete</Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <UploadPreviewModal
                uploadState={uploadState}
                onClose={() => !uploadState.uploading && setUploadState(prev => ({ ...prev, open: false }))}
                onConfirm={handleUploadConfirm}
            />

            <GithubImportModal
                open={githubModal.open}
                mode={githubModal.mode}
                onClose={() => setGithubModal(prev => ({ ...prev, open: false }))}
                onSuccess={handleGithubSuccess}
            />
            <SyncPickerModal
                open={syncPickerOpen}
                resourceType="skill"
                onClose={() => { setSyncPickerOpen(false); loadSkills(); }}
                onSuccess={(msg) => {
                    setSuccessMessage(msg);
                    loadSkills();
                    setTimeout(() => setSuccessMessage(null), 6000);
                }}
            />

        </div>
    );
}

function UploadPreviewModal({ uploadState, onClose, onConfirm }) {
    const preview = uploadState.preview;
    const isExisting = preview?.exists && preview?.existingSkill;

    return (
        <Modal open={uploadState.open} onClose={onClose} title="Upload Skill" size="md">
            <Modal.Body>
                {preview?.loading ? (
                    <Spinner size="md" text="Validating..." center />
                ) : preview && (
                    <div className="space-y-4">
                        {isExisting && (
                            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                                <div className="flex items-center gap-2 mb-3">
                                    <svg className="w-4 h-4 text-amber-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                                    </svg>
                                    <span className="text-sm font-medium text-amber-800">Skill already exists</span>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="rounded border border-amber-200 bg-white p-2.5">
                                        <div className="text-xs font-medium text-gray-500 mb-1.5">Current version</div>
                                        <div className="text-sm font-medium text-gray-900 truncate">{preview.existingSkill.name}</div>
                                        <div className="text-xs text-gray-400 mt-0.5"><Badge variant="outline" className="text-xs">{preview.existingSkill.id}</Badge></div>
                                        {preview.existingSkill.description && (
                                            <div className="text-xs text-gray-500 mt-1.5 line-clamp-2">{preview.existingSkill.description}</div>
                                        )}
                                        <div className="text-xs text-gray-400 mt-1.5">{preview.existingSkill.resource_count} resource{preview.existingSkill.resource_count !== 1 ? 's' : ''}</div>
                                    </div>
                                    <div className="rounded border border-blue-200 bg-blue-50 p-2.5">
                                        <div className="text-xs font-medium text-blue-600 mb-1.5">New version</div>
                                        <div className="text-sm font-medium text-gray-900 truncate">{preview.skillName}</div>
                                        <div className="text-xs text-gray-400 mt-0.5"><Badge variant="outline" className="text-xs">{preview.skillId}</Badge></div>
                                        {preview.skillDescription && (
                                            <div className="text-xs text-gray-500 mt-1.5 line-clamp-2">{preview.skillDescription}</div>
                                        )}
                                        <div className="text-xs text-gray-400 mt-1.5">{preview.files.length} file{preview.files.length !== 1 ? 's' : ''} · {formatSize(preview.totalSize)}</div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {!isExisting && preview.skillName && (
                            <div className="space-y-1">
                                <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium text-gray-900">{preview.skillName}</span>
                                    {preview.skillId && <Badge variant="outline">{preview.skillId}</Badge>}
                                </div>
                                {preview.skillDescription && (
                                    <p className="text-xs text-gray-500 line-clamp-2">{preview.skillDescription}</p>
                                )}
                            </div>
                        )}

                        {!isExisting && (
                            <div>
                                <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
                                    <span>{preview.files.length} files</span>
                                    <span>{formatSize(preview.totalSize)}</span>
                                </div>
                                <div className="border border-gray-200 rounded-lg max-h-60 overflow-y-auto">
                                    {preview.files.map((f, i) => (
                                        <div key={i} className="flex items-center justify-between px-3 py-1.5 text-xs border-b border-gray-50 last:border-0">
                                            <span className="text-gray-700 font-mono truncate">{f.path}</span>
                                            <span className="text-gray-400 shrink-0 ml-2">{formatSize(f.size)}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {uploadState.error && (
                            <Alert variant="error">{uploadState.error}</Alert>
                        )}
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="ghost" onClick={onClose} disabled={uploadState.uploading}>Cancel</Button>
                {isExisting ? (
                    <Button
                        className="bg-red-600 hover:bg-red-700 text-white border-red-600"
                        onClick={() => onConfirm(true)}
                        loading={uploadState.uploading}
                        disabled={!!uploadState.error}
                    >
                        Yes, Override
                    </Button>
                ) : (
                    <Button
                        onClick={() => onConfirm(false)}
                        loading={uploadState.uploading}
                        disabled={!!uploadState.error || preview?.loading}
                    >
                        Upload
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    );
}

function formatSize(bytes) {
    if (!bytes) return '0 B';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
